import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';
import { GSAssessmentContent } from './GSAssessmentContent';
import { GSCheckList } from './GSCheckList';
import { type GsDecision, useGetGsAssessment } from '../../../graphql';
import { PAGE_TEXT } from '../../../lang';
import { ApplicantParticulars } from '../../../DRCAssessment';
import { AssessmentSidebar, ASSESSMENT_MODULE } from '../../../components';
import { getApplicationStatus } from '../../../../Nomination/graphql';
import {
  type SidebarData,
  Button,
  Loading,
  Alert,
  Stepper,
  type Steps,
  UserRole,
} from '../../../../../components';
import { APPLICATION_STATUS_TYPE } from '../../../../../utils/statusConstants/statusConstants';

export const GSAssessmentDetail: React.FunctionComponent = () => {
  const { applicationId = '' } = useParams();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [activatedModuleId, setActivatedModuleId] = useState(
    ASSESSMENT_MODULE.GS_ASSESSMENT,
  );

  const {
    applicationStatus,
    loading: applicationStatusLoading,
    error,
  } = getApplicationStatus(applicationId);

  // if the application is not being assessed by gs, redirect to monitor
  if (
    !applicationStatusLoading &&
    applicationStatus !== APPLICATION_STATUS_TYPE.UNDER_GS &&
    applicationStatus !== APPLICATION_STATUS_TYPE.OFFER_PROVISIONING
  ) {
    navigate(`/internal/monitor/${applicationId}`);
  }

  const handleChangeActivated = (activatedId: string): void => {
    setActivatedModuleId(activatedId as ASSESSMENT_MODULE);
  };

  const {
    gsDecision,
    hadGsChecked,
    remarks,
    department,
    previewData: initialPreviewData,
    loading: gsDecisionLoading,
    error: gsDecisionError,
    refetch: refetchGsDecision,
  } = useGetGsAssessment(applicationId);

  const { remarks: gsRemarks, ...others } = gsDecision;

  const reversedRemarks = [...remarks].reverse();
  const lastGsRemark =
    reversedRemarks.find(rmk => rmk.fromRole === UserRole.GS)?.remark ?? '';

  const gsDecisionValues: GsDecision = {
    remarks: lastGsRemark,
    ...others,
  };

  const sidebarData: SidebarData[] = [
    {
      id: ASSESSMENT_MODULE.PARTICULARS,
      name: PAGE_TEXT.GS_ASSESSMENT_DETAILS.PARTICULARS.TITLE,
    },
    {
      id: ASSESSMENT_MODULE.GS_ASSESSMENT,
      name: PAGE_TEXT.GS_ASSESSMENT_DETAILS.ASSESSMENT.TITLE,
    },
  ];

  const STEPPER_TEXT = PAGE_TEXT.GS_ASSESSMENT_DETAILS.CHECK_STEPPER;
  const assessmentModulesMap: Partial<
    Record<ASSESSMENT_MODULE, { component: any; moduleTitle: string }>
  > = {
    [ASSESSMENT_MODULE.PARTICULARS]: {
      component: <ApplicantParticulars showGoDetail />,
      moduleTitle: PAGE_TEXT.GS_ASSESSMENT_DETAILS.PARTICULARS.TITLE,
    },
    [ASSESSMENT_MODULE.GS_ASSESSMENT]: {
      component:
        activeStep === 0 ? (
          <GSCheckList
            title={STEPPER_TEXT.OFFER_CHECK.TITLE}
            department={department}
            hadGsChecked={hadGsChecked}
            refetchGsChecked={refetchGsDecision}
          />
        ) : (
          <GSAssessmentContent
            title={STEPPER_TEXT.OFFER_DECISION.TITLE}
            initialValues={gsDecisionValues}
            previewData={initialPreviewData}
            remarks={remarks}
            refetchGsDecision={refetchGsDecision}
          />
        ),
      moduleTitle: PAGE_TEXT.GS_ASSESSMENT_DETAILS.ASSESSMENT.TITLE,
    },
  };

  const offerSteps: Steps[] = [
    {
      label: STEPPER_TEXT.OFFER_CHECK.TITLE,
      description: STEPPER_TEXT.OFFER_CHECK.DESCRIPTION,
      hasDone: hadGsChecked,
      clickableStep: true,
    },
    {
      label: STEPPER_TEXT.OFFER_DECISION.TITLE,
      description: STEPPER_TEXT.OFFER_DECISION.DESCRIPTION,
      hasDone: Boolean(gsDecision?.secondCheckedAt),
      clickableStep: hadGsChecked,
    },
  ];

  const activatedModule:
    | { component: React.ReactNode; moduleTitle: string }
    | undefined = assessmentModulesMap[activatedModuleId];

  const moduleComponent = activatedModule?.component ?? activatedModuleId;
  const moduleTitle = activatedModule?.moduleTitle ?? activatedModuleId;

  const ifShowStepper: boolean =
    moduleTitle === PAGE_TEXT.GS_ASSESSMENT_DETAILS.ASSESSMENT.TITLE;

  const handleBack = (): void => {
    window.location.href = '/internal/gs-homepage/gsassessments';
  };

  if (error ?? gsDecisionError) {
    return (
      <Alert
        openAlert={true}
        setOpenAlert={() => {}}
        alertDetails={{
          alertContent:
            `Error Occurs.` +
            (error as Error)?.message +
            (gsDecisionError as Error)?.message,
          alertType: 'error',
        }}
      />
    );
  }

  const handleStepperChange = (newStep: number): void => {
    setActiveStep(newStep);
  };

  const loading = gsDecisionLoading || applicationStatusLoading;

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <PageContainer>
          <div className="sidebar-area">
            <AssessmentSidebar
              onChangeActivated={handleChangeActivated}
              collapsible={true}
              currentActivatedModule={activatedModuleId}
              sidebarData={sidebarData}
              drawerWidth={240}
              drawerInnerWidth="14.75rem"
              buttonField={
                <div className="flex pt-12 justify-center w-full">
                  <Button variant="outlined" onClick={handleBack}>
                    {PAGE_TEXT.BACK}
                  </Button>
                </div>
              }
            />
          </div>

          <Container>
            <h1>{moduleTitle}</h1>
            <div className="flex flex-row w-full p-2">
              <div className="form-area flex-1 p-4">{moduleComponent}</div>
              {ifShowStepper && (
                <div className="mt-6 px-2">
                  <Stepper
                    activeStep={activeStep}
                    steps={offerSteps}
                    onChange={handleStepperChange}
                    hideButton={!hadGsChecked}
                  />
                </div>
              )}
            </div>
          </Container>
        </PageContainer>
      )}
    </>
  );
};

const PageContainer = styled.div`
  display: flex;
  flex-direction: row;

  .sidebar-area {
    width: min-content;
  }

  h1 {
    margin-top: 2rem;
    font-size: 1.5rem;
    font-weight: 700;
    text-align: center;
    color: #951d1d;
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
`;
